import { CompanyType } from '../../../__generated__/graphql'
import utmSourceMapping from '../../../json/utmsource.json'

const PAID_SOCIAL_SOURCE_TOKENS = ['FB', 'FACEBOOK', 'IG', 'INSTAGRAM', 'SOCIAL']
const PAID_MEDIUM_TOKEN_REGEX = /(?:^|[^A-Z])PAID(?:[^A-Z]|$)/

// Type definitions
interface TeaserData {
  key: string
  name: string
  tdImage?: string
  trafficDriverId: string
  status?: string
  notInitialized?: boolean
  children: any[]
  [key: string]: any
}

// Helper functions from utils/helperfunctions.js
const sanitize = (value: number) => isNaN(value) ? 0 : value
const roundTo = (value: number, decimals: number) => Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)

const getCommonProperties = ({
  reads = 0, 
  totalReadtime = 0, 
  totalCost = 0, 
  tdClicks = 0, 
  publisherRevenue = 0, 
  tdImpressions = 0, 
  clickOuts = 0, 
  conversions = 0, 
  type = null,
  companyType = null as string | null
} = {}) => {
  const baseProperties = {
    avgReadtime: reads > 0 ? roundTo((totalReadtime / 1000) / reads, 1) : 0,
    clickOuts: clickOuts,
    conversions: conversions,
    cotr: reads > 0 ? roundTo((clickOuts / reads) * 100, 2) : 0,
    ctr: tdImpressions > 0 ? roundTo((tdClicks / tdImpressions) * 100, 2) : 0,
    imps_reads: tdImpressions > 0 ? roundTo((reads / tdImpressions) * 1000, 2) : 0,
    reads: reads,
    tdClicks: tdClicks,
    tdImpressions: tdImpressions,
    totalReadtime: roundTo(totalReadtime, 2),
    type,
    tdType: type,
  }

  // Add company-specific properties
  if (companyType === CompanyType.Publisher) {
    return {
      ...baseProperties,
      publisherRevenue: roundTo(publisherRevenue, 2),
      eCpm: tdImpressions > 0 ? roundTo(publisherRevenue / (tdImpressions / 1000), 2) : 0,
      rpa: conversions > 0 ? roundTo(publisherRevenue / conversions, 2) : 0,
    }
  } else {
    // Advertiser or Agency
    return {
      ...baseProperties,
      totalCost: roundTo(totalCost, 2),
      cpa: conversions > 0 ? roundTo(totalCost / conversions, 2) : 0,
      eCpm: tdImpressions > 0 ? roundTo(totalCost / (tdImpressions / 1000), 2) : 0,
    }
  }
}

const getParsedData = (item: any) => ({
  clickOuts: sanitize(parseFloat(item['aggregateTdLevel.clickouts'])),
  conversions: sanitize(parseFloat(item['aggregateTdLevel.conversions'])),
  publisherRevenue: sanitize(parseFloat(item['aggregateTdLevel.publisherRevenue'])),
  reads: sanitize(parseFloat(item['aggregateTdLevel.reads'])),
  tdClicks: sanitize(parseFloat(item['aggregateTdLevel.tdClicks'])),
  tdImpressions: sanitize(parseFloat(item['aggregateTdLevel.tdImpressions'])),
  totalCost: sanitize(parseFloat(item['aggregateTdLevel.totalCost'])),
  totalReadtime: sanitize(parseFloat(item['aggregateTdLevel.total_readtime'])),
  type: item['aggregateTdLevel.tdType'],
})

// Export getParsedData for use in other files
export { getParsedData }

const addStatistics = (currentData: any, newData: any, companyType?: string) => {
  currentData.clickOuts += newData.clickOuts
  currentData.conversions += newData.conversions
  currentData.reads += newData.reads
  currentData.tdClicks += newData.tdClicks
  currentData.tdImpressions += newData.tdImpressions
  currentData.totalReadtime += newData.totalReadtime

  // Add company-specific fields
  if (currentData.publisherRevenue !== undefined) {
    currentData.publisherRevenue = roundTo(currentData.publisherRevenue + newData.publisherRevenue, 2)
  }
  if (currentData.totalCost !== undefined) {
    currentData.totalCost = roundTo(currentData.totalCost + newData.totalCost, 2)
  }

  // Recalculate derived metrics
  currentData.avgReadtime = currentData.reads > 0 ? roundTo((currentData.totalReadtime / 1000) / currentData.reads, 1) : 0
  currentData.cotr = currentData.reads > 0 ? roundTo((currentData.clickOuts / currentData.reads) * 100, 2) : 0
  currentData.ctr = currentData.tdImpressions > 0 ? roundTo((currentData.tdClicks / currentData.tdImpressions) * 100, 2) : 0
  currentData.imps_reads = currentData.tdImpressions > 0 ? roundTo((currentData.reads / currentData.tdImpressions) * 1000, 2) : 0
  
  // Company-specific derived metrics
  if (currentData.publisherRevenue !== undefined) {
    currentData.eCpm = currentData.tdImpressions > 0 ? roundTo(currentData.publisherRevenue / (currentData.tdImpressions / 1000), 2) : 0
    currentData.rpa = currentData.conversions > 0 ? roundTo(currentData.publisherRevenue / currentData.conversions, 2) : 0
  }
  if (currentData.totalCost !== undefined) {
    currentData.cpa = currentData.conversions > 0 ? roundTo(currentData.totalCost / currentData.conversions, 2) : 0
    // Calculate eCPM for advertisers/agencies using totalCost
    currentData.eCpm = currentData.tdImpressions > 0 ? roundTo(currentData.totalCost / (currentData.tdImpressions / 1000), 2) : 0
  }
}




// Transform channel data from real cube data
export const transformChannelDataFromCube = (rawData: any[], publisherWebsiteIds: string[] | null = null, breakdownType: 'website' | 'source' = 'website') => {
  if (!rawData || !Array.isArray(rawData)) {
    return []
  }

  const channelMap = new Map()
  
  // Filter data by publisher websites if specified
  const filteredData = publisherWebsiteIds 
    ? rawData.filter(item => publisherWebsiteIds.includes(item['aggregateTdLevel.websiteId']))
    : rawData
  
  filteredData.forEach((item: any) => {
    const rawUtmSource = (item['aggregateTdLevel.utmSource'] || '').toString().trim()
    const rawUtmMedium = (item['aggregateTdLevel.utmMedium'] || '').toString().trim()
    const utmSourceUpper = rawUtmSource.toUpperCase()
    const utmMediumUpper = rawUtmMedium.toUpperCase()

    let utmSource = rawUtmSource
    const websiteId = item['aggregateTdLevel.websiteId']
    const websiteName = item['aggregateTdLevel.websiteName']

    const isPaidSocialSource = PAID_SOCIAL_SOURCE_TOKENS.some(token =>
      utmSourceUpper.includes(token)
    )
    const isPaidInMedium = PAID_MEDIUM_TOKEN_REGEX.test(utmMediumUpper)
    const isPaidSocial = isPaidInMedium && isPaidSocialSource
    
    // Handle teasers (empty utm source or "ADSERVER") as a separate channel
    if (utmSource === '' || utmSource === 'ADSERVER') {
      utmSource = 'TEASERS'
    }
    
    // Map utm sources to channel names
    let channelName: string
    if (isPaidSocial) {
      channelName = 'Social Paid'
    } else if (utmSource === 'TEASERS') {
      channelName = 'Teasers'
    } else if (utmSource === 'ORGANIC') {
      channelName = 'Organic'
    } else if (utmSource === 'SOCIAL') {
      channelName = 'Social'
    } else if (utmSource === 'DIRECT') {
      channelName = 'Direct'
    } else if (utmSource === 'INTERNAL_REFERRAL') {
      channelName = 'Internal Referral'
    } else if (utmSource === 'EXTERNAL_REFERRAL') {
      channelName = 'External Referral'
    } else {
      channelName = 'Other'
    }

    const parsedData = getParsedData(item)
    
    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, {
        key: channelName.toLowerCase().replace(/\s+/g, '_'),
        channel: channelName,
        reads: 0,
        totalReadtime: 0,
        clickOuts: 0,
        conversions: 0,
        children: []
      })
    }
    
    const channel = channelMap.get(channelName)
    
    // Add to channel totals
    channel.reads += parsedData.reads
    channel.totalReadtime += parsedData.totalReadtime
    channel.clickOuts += parsedData.clickOuts
    channel.conversions += parsedData.conversions
    
    // Create child based on breakdown type
    if (breakdownType === 'source') {
      // Group by source using utmMedium
      const originalUtmMedium = item['aggregateTdLevel.utmMedium'] || 'Direct'
      // Convert to lowercase for lookup, then look up the source name from the mapping
      const lookupKey = originalUtmMedium.toLowerCase()
      const sourceName = (utmSourceMapping as any)[lookupKey] || originalUtmMedium
      
      // Find child by the original utmMedium value to maintain uniqueness
      let sourceChild = channel.children.find((child: any) => child.mediumKey === originalUtmMedium)
      if (!sourceChild) {
        sourceChild = {
          key: `${channel.key}_${originalUtmMedium.replace(/\s+/g, '_')}`,
          source: sourceName,
          mediumKey: originalUtmMedium, // Store original medium for uniqueness
          website: null,
          websiteId: null,
          reads: 0,
          totalReadtime: 0,
          clickOuts: 0,
          conversions: 0
        }
        channel.children.push(sourceChild)
      }
      
      // Add to source totals
      sourceChild.reads += parsedData.reads
      sourceChild.totalReadtime += parsedData.totalReadtime
      sourceChild.clickOuts += parsedData.clickOuts
      sourceChild.conversions += parsedData.conversions
    } else {
      // Group by website (existing logic)
      let websiteChild = channel.children.find((child: any) => child.websiteId === websiteId)
      if (!websiteChild) {
        websiteChild = {
          key: `${channel.key}_${websiteId}`,
          website: websiteName,
          websiteId: websiteId,
          source: null,
          reads: 0,
          totalReadtime: 0,
          clickOuts: 0,
          conversions: 0
        }
        channel.children.push(websiteChild)
      }
      
      // Add to website totals
      websiteChild.reads += parsedData.reads
      websiteChild.totalReadtime += parsedData.totalReadtime
      websiteChild.clickOuts += parsedData.clickOuts
      websiteChild.conversions += parsedData.conversions
    }
  })

  // Calculate average readtime for all channels and their children
  const channelsWithAvgReadtime = Array.from(channelMap.values()).map(channel => {
    // Calculate average readtime for main channel
    const channelWithAvg = {
      ...channel,
      avgReadtime: channel.reads > 0 ? roundTo((channel.totalReadtime / 1000) / channel.reads, 1) : 0
    }
    
    // Calculate average readtime for children (website or source)
    channelWithAvg.children = channel.children.map(child => ({
      ...child,
      avgReadtime: child.reads > 0 ? roundTo((child.totalReadtime / 1000) / child.reads, 1) : 0
    }))
    
    return channelWithAvg
  })
  
  return channelsWithAvgReadtime.sort((a, b) => b.reads - a.reads)
}

// Transform teaser data from real cube data
export const transformTeaserDataFromCube = (
  teasers: any[], 
  cubeData: any[], 
  companyType: string, 
  publisherWebsiteIds: string[] | null = null
) => {
  // Create a map of active teasers
  const activeTeasersMap = new Map()
  teasers.forEach(teaser => {
    activeTeasersMap.set(teaser._id, teaser)
  })

  const totals = companyType === CompanyType.Publisher ? {
    reads: 0,
    tdClicks: 0,
    tdImpressions: 0,
    publisherRevenue: 0,
    eCpm: 0,
    totalReadtime: 0,
    avgReadtime: 0,
    clickOuts: 0,
    imps_reads: 0,
    ctr: 0,
    cotr: 0,
    conversions: 0,
  } : {
    totalCost: 0,
    reads: 0,
    tdClicks: 0,
    tdImpressions: 0,
    cpa: 0,
    eCpm: 0,
    totalReadtime: 0,
    avgReadtime: 0,
    clickOuts: 0,
    imps_reads: 0,
    ctr: 0,
    cotr: 0,
    conversions: 0,
  }

  // Initialize with active teasers
  const newTeasers: TeaserData[] = teasers.map((teaser) => {
    // Filter websites by publisher if specified
    const filteredWebsites = publisherWebsiteIds 
      ? teaser.websites.filter((website: any) => publisherWebsiteIds.includes(website._id))
      : teaser.websites

    // Ensure children is always an array
    const children = filteredWebsites.map((website: any) => ({
      key: `${teaser._id}${website._id}`,
      name: website.name,
      websiteId: website._id,
      ...getCommonProperties({ companyType }),
    }))

    return {
      key: teaser._id,
      name: teaser.headline,
      tdImage: teaser.imageId,
      trafficDriverId: teaser._id,
      status: teaser.status,
      notInitialized: true,
      ...getCommonProperties({ companyType }),
      children: children || [],
    }
  }).filter(teaser => teaser.children.length > 0) // Remove teasers with no accessible websites

  // Process cube data - filter out only teaser data (empty utm source or "ADSERVER")
  // Also filter by publisher websites if specified
  const teaserCubeData = cubeData.filter(item => {
    const utmSource = item['aggregateTdLevel.utmSource']
    const websiteId = item['aggregateTdLevel.websiteId']
    const isTeaserData = utmSource === '' || utmSource === 'ADSERVER'
    const isPublisherWebsite = !publisherWebsiteIds || publisherWebsiteIds.includes(websiteId)
    return isTeaserData && isPublisherWebsite
  })

  const teaserData = teaserCubeData.reduce((acc, item) => {
    const trafficDriverId = item['aggregateTdLevel.trafficDriverId']
    const teaserIndex = acc.findIndex(t => t.trafficDriverId === trafficDriverId)
    const parsedData = getParsedData(item)

    if (teaserIndex !== -1) {
      // Found existing teaser
      const teaser = acc[teaserIndex]
      const websiteIndex = teaser.children.findIndex(
        w => w.websiteId === item['aggregateTdLevel.websiteId']
      )

      if (websiteIndex !== -1) {
        // Update existing website
        teaser.children[websiteIndex] = {
          ...teaser.children[websiteIndex],
          ...getCommonProperties({ ...parsedData, companyType })
        }
      } else {
        // Add new website to existing teaser
        teaser.children.push({
          key: `${trafficDriverId}${item['aggregateTdLevel.websiteId']}`,
          name: item['aggregateTdLevel.websiteName'],
          websiteId: item['aggregateTdLevel.websiteId'],
          ...getCommonProperties({ ...parsedData, companyType })
        })
      }

      if (teaser.notInitialized) {
        teaser.notInitialized = undefined
        acc[teaserIndex] = {
          ...teaser,
          ...getCommonProperties({ ...parsedData, companyType })
        }
      } else {
        addStatistics(teaser, parsedData, companyType)
      }
    } else {
      // Create new teaser (removed teaser)
      const activeTeaser = activeTeasersMap.get(trafficDriverId)
      const teaserName = item['aggregateTdLevel.tdHeadline'] || 'Unknown Teaser'
      
      acc.push({
        key: trafficDriverId,
        name: `${teaserName} (Removed)`,
        tdImage: item['aggregateTdLevel.tdImage'] || undefined,
        trafficDriverId: trafficDriverId,
        removed: true,
        ...getCommonProperties({ ...parsedData, companyType }),
        children: [{
          key: `${trafficDriverId}${item['aggregateTdLevel.websiteId']}`,
          name: item['aggregateTdLevel.websiteName'],
          websiteId: item['aggregateTdLevel.websiteId'],
          ...getCommonProperties({ ...parsedData, companyType })
        }]
      })
    }

    // Update totals
    addStatistics(totals, parsedData)

    return acc
  }, newTeasers)

  // Sort teasers by impressions descending by default
  const sortedTeaserData = teaserData.sort((a, b) => (b.tdImpressions || 0) - (a.tdImpressions || 0))
  
  return { teaserData: sortedTeaserData, totals }
}

